/**
 * Team Inbox — S6 Inbox Settings (UI Spec §7.7 — Admin only, gated by the
 * `teaminbox.settings` capability; the server re-checks every write).
 *
 * Four sections per the spec:
 *   1 Numbers          — registry editor incl. the disabled 7121 row
 *                        ("Pending Meta approval") + Add number (R-A2:
 *                        a new line is ONE config entry, zero code)
 *   2 Pools & allocation — pool members per number · round-robin ON/OFF ·
 *                        escalation timeout (default 15) · fallback chain
 *                        (default Khilendra → Rohit) · working hours
 *   3 Notifications    — Admin scope (owner-locked default: escalations +
 *                        unassigned) · quiet hours · per-user desktop
 *                        mute (THIS device — browser stop-gap)
 *   4 Canned replies   — CRUD only; "/" composer insert is Phase 4
 *
 * All validation is the pure lib/settingsModel.ts (test-locked); writes go
 * through `teamInboxAdmin · settingsUpsert` so no rules change is needed
 * before merge.
 */
import { useMemo, useState } from 'react';
import type {
  CannedReply,
  InboxMeta,
  Teammate,
} from '../lib/types';
import {
  draftFromRegistry,
  draftToRegistryDoc,
  poolHasMember,
  togglePoolMember,
  validateDraft,
  type SettingsDraft,
} from '../lib/settingsModel';
import { CannedManager } from '../components/CannedManager';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function InboxSettings({
  meta,
  notifyEnabled,
  notifyPermission,
  onToggleNotify,
  onSaveRegistry,
  onSaveCanned,
  onClose,
}: {
  meta: InboxMeta;
  /** Per-user desktop-notification mute state (this device). */
  notifyEnabled: boolean;
  notifyPermission: string;
  onToggleNotify: (enabled: boolean) => void;
  onSaveRegistry: (doc: unknown) => Promise<boolean>;
  onSaveCanned: (items: CannedReply[]) => Promise<boolean>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<SettingsDraft>(() =>
    draftFromRegistry(meta.registry),
  );
  const [canned, setCanned] = useState<CannedReply[]>(meta.canned);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const errors = useMemo(() => validateDraft(draft), [draft]);
  const teammates: Teammate[] = meta.teammates;

  function patch(p: Partial<SettingsDraft>) {
    setDraft((d) => ({ ...d, ...p }));
    setSaved(null);
  }
  function patchNumber(i: number, p: Partial<SettingsDraft['numbers'][0]>) {
    setDraft((d) => ({
      ...d,
      numbers: d.numbers.map((n, idx) => (idx === i ? { ...n, ...p } : n)),
    }));
    setSaved(null);
  }
  function togglePool(i: number, mate: Teammate) {
    setDraft((d) => ({
      ...d,
      numbers: d.numbers.map((n, idx) =>
        idx === i ? { ...n, pool: togglePoolMember(n.pool, mate) } : n,
      ),
    }));
    setSaved(null);
  }

  async function saveRegistry() {
    if (errors.length) return;
    setSaving(true);
    setSaveErr(null);
    const ok = await onSaveRegistry(draftToRegistryDoc(draft));
    setSaving(false);
    if (ok) setSaved('Allocation settings saved.');
    else setSaveErr('Save failed — check your connection and try again.');
  }

  // Persist the WHOLE canned array (the CannedManager hands back the full
  // next-state). Mirrors local state on success so the manager re-renders.
  async function saveCannedItems(next: CannedReply[]): Promise<boolean> {
    setSaving(true);
    const ok = await onSaveCanned(next);
    setSaving(false);
    if (ok) {
      setCanned(next);
      setSaved('Canned messages saved.');
    } else {
      setSaveErr('Save failed — try again.');
    }
    return ok;
  }

  const enabledNumbers = draft.numbers.filter((n) => n.enabled && n.phoneId);

  return (
    <div className="ti-settings" data-testid="ti-settings">
      <div className="ti-settings-head">
        <h2>Inbox Settings</h2>
        <button type="button" className="ti-back" onClick={onClose} aria-label="Close settings">
          ✕
        </button>
      </div>
      <div className="ti-settings-body">
        {/* ── 1 · Numbers ───────────────────────────────────────── */}
        <section>
          <h3>Numbers</h3>
          <p className="hint">
            One registry drives every chip, filter and pool. Adding a line is
            config only — paste its Meta phone ID and enable it.
          </p>
          {draft.numbers.map((n, i) => (
            <div className="ti-set-number" key={i}>
              <span className="swatch" style={{ background: n.colour }} aria-hidden />
              <label>
                Label
                <input
                  value={n.label}
                  onChange={(e) => patchNumber(i, { label: e.target.value })}
                />
              </label>
              <label>
                Line
                <input
                  value={n.line}
                  onChange={(e) => patchNumber(i, { line: e.target.value })}
                />
              </label>
              <label>
                Phone ID
                <input
                  value={n.phoneId}
                  placeholder="after the QR scan"
                  onChange={(e) => patchNumber(i, { phoneId: e.target.value })}
                />
              </label>
              <label>
                Colour
                <input
                  value={n.colour}
                  onChange={(e) => patchNumber(i, { colour: e.target.value })}
                />
              </label>
              <label className="chk">
                <input
                  type="checkbox"
                  checked={n.enabled}
                  disabled={!n.phoneId}
                  onChange={(e) => patchNumber(i, { enabled: e.target.checked })}
                />
                Enabled
              </label>
              {!n.phoneId && (
                <span className="ti-chip pending">Pending Meta approval</span>
              )}
            </div>
          ))}
          <button
            type="button"
            className="ti-set-add"
            onClick={() =>
              patch({
                numbers: [
                  ...draft.numbers,
                  {
                    phoneId: '',
                    line: '',
                    label: '',
                    colour: '#6b7280',
                    pool: [],
                    enabled: false,
                  },
                ],
              })
            }
          >
            ＋ Add number
          </button>
        </section>

        {/* ── 2 · Pools & allocation ────────────────────────────── */}
        <section>
          <h3>Pools &amp; allocation</h3>
          {enabledNumbers.map((n) => {
            const i = draft.numbers.indexOf(n);
            return (
              <div className="ti-set-pool" key={n.phoneId}>
                <b>{n.label} pool</b>
                <div className="members">
                  {teammates.map((t) => (
                    <label className="chk" key={t.id}>
                      <input
                        type="checkbox"
                        checked={poolHasMember(n.pool, t)}
                        onChange={() => togglePool(i, t)}
                      />
                      {t.name}
                      <span
                        className={`dot ${t.available ? 'on' : 'off'}`}
                        title={t.available ? 'Available' : 'Away'}
                        aria-hidden
                      />
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="ti-set-grid">
            <label className="chk">
              <input
                type="checkbox"
                checked={draft.mode === 'round_robin'}
                onChange={(e) =>
                  patch({ mode: e.target.checked ? 'round_robin' : 'manual' })
                }
              />
              Round-robin auto-allocation
            </label>
            <label>
              Escalation timeout (min)
              <input
                type="number"
                min={1}
                max={240}
                value={draft.escalateAfterMin}
                onChange={(e) =>
                  patch({ escalateAfterMin: Number(e.target.value) })
                }
              />
            </label>
            <label>
              Fallback chain (in order)
              <select
                value={draft.fallback[0] || ''}
                onChange={(e) =>
                  patch({ fallback: [e.target.value, draft.fallback[1] || ''] })
                }
              >
                {teammates.map((t) => (
                  <option key={t.id} value={t.id}>
                    1 · {t.name}
                  </option>
                ))}
                {!teammates.some((t) => t.id === draft.fallback[0]) &&
                  draft.fallback[0] && (
                    <option value={draft.fallback[0]}>
                      1 · {draft.fallback[0]}
                    </option>
                  )}
              </select>
              <select
                value={draft.fallback[1] || ''}
                onChange={(e) =>
                  patch({
                    fallback: [draft.fallback[0] || '', e.target.value].filter(
                      Boolean,
                    ),
                  })
                }
              >
                <option value="">2 · (none)</option>
                {teammates.map((t) => (
                  <option key={t.id} value={t.id}>
                    2 · {t.name}
                  </option>
                ))}
                {!teammates.some((t) => t.id === draft.fallback[1]) &&
                  draft.fallback[1] && (
                    <option value={draft.fallback[1]}>
                      2 · {draft.fallback[1]}
                    </option>
                  )}
              </select>
            </label>
            <label className="chk">
              <input
                type="checkbox"
                checked={draft.workingHoursOn}
                onChange={(e) => patch({ workingHoursOn: e.target.checked })}
              />
              Working hours (outside → fallback chain)
            </label>
            {draft.workingHoursOn && (
              <div className="ti-set-hours">
                <input
                  value={draft.whStart}
                  aria-label="Working hours start"
                  onChange={(e) => patch({ whStart: e.target.value })}
                />
                –
                <input
                  value={draft.whEnd}
                  aria-label="Working hours end"
                  onChange={(e) => patch({ whEnd: e.target.value })}
                />
                <span className="days">
                  {DAY_LABELS.map((d, idx) => (
                    <label className="chk" key={d}>
                      <input
                        type="checkbox"
                        checked={draft.whDays.includes(idx)}
                        onChange={() =>
                          patch({
                            whDays: draft.whDays.includes(idx)
                              ? draft.whDays.filter((x) => x !== idx)
                              : [...draft.whDays, idx],
                          })
                        }
                      />
                      {d}
                    </label>
                  ))}
                </span>
              </div>
            )}
          </div>
        </section>

        {/* ── 3 · Notifications ─────────────────────────────────── */}
        <section>
          <h3>Notifications</h3>
          <label>
            Admin scope
            <select
              value={draft.adminScope}
              onChange={(e) =>
                patch({
                  adminScope:
                    e.target.value === 'all' ? 'all' : 'escalations_unassigned',
                })
              }
            >
              <option value="escalations_unassigned">
                Escalations + unassigned only (default)
              </option>
              <option value="all">Every message</option>
            </select>
          </label>
          <label className="chk">
            <input
              type="checkbox"
              checked={draft.quietHoursOn}
              onChange={(e) => patch({ quietHoursOn: e.target.checked })}
            />
            Quiet hours
          </label>
          {draft.quietHoursOn && (
            <div className="ti-set-hours">
              <input
                value={draft.qhStart}
                aria-label="Quiet hours start"
                onChange={(e) => patch({ qhStart: e.target.value })}
              />
              –
              <input
                value={draft.qhEnd}
                aria-label="Quiet hours end"
                onChange={(e) => patch({ qhEnd: e.target.value })}
              />
            </div>
          )}
          <label className="chk">
            <input
              type="checkbox"
              checked={notifyEnabled}
              onChange={(e) => onToggleNotify(e.target.checked)}
            />
            Desktop notifications on THIS device
            {notifyPermission !== 'granted' && notifyEnabled && (
              <span className="hint"> (browser permission: {notifyPermission})</span>
            )}
          </label>
        </section>

        {/* ── 4 · Canned messages (manager) ─────────────────────── */}
        <section>
          <CannedManager
            items={canned}
            myStaffId={meta.me.staffId}
            saving={saving}
            onSave={saveCannedItems}
          />
        </section>

        {/* ── save bar ──────────────────────────────────────────── */}
        <div className="ti-set-save">
          {errors.map((e) => (
            <div className="ti-set-err" key={e} role="alert">
              {e}
            </div>
          ))}
          {saveErr && (
            <div className="ti-set-err" role="alert">
              {saveErr}
            </div>
          )}
          {saved && <div className="ti-set-ok">{saved}</div>}
          <button
            type="button"
            className="ti-set-savebtn"
            disabled={saving || errors.length > 0}
            onClick={() => void saveRegistry()}
          >
            {saving ? 'Saving…' : 'Save allocation settings'}
          </button>
        </div>
      </div>
    </div>
  );
}
